import { Guid, NotNullish } from '@breezy/shared'
import { faClockRotateLeft } from '@fortawesome/pro-regular-svg-icons'
import { faStar } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useQuery } from 'urql'
import { PricebookItemFragment } from '../generated/user/graphql'
import { useToggleFavoritePricebookItem } from '../hooks/useToggleFavoritePricebookItem'
import { useStrictContext } from '../utils/react-utils'
import { GET_FAVORITE_PRICEBOOK_ITEMS, GET_PRICEBOOK_DATA, GET_RECENT_PRICEBOOK_ITEMS } from './PricebookProvider.gql'
import { useExpectedPrincipal } from './PrincipalUser'

export type PricebookPickerItem = {
  id: Guid
  name: string
  subText?: string
  value: number
  ignoreSearch?: boolean
  tags?: Array<{
    name: string
    icon?: React.ReactNode
  }>
  cartItemVariant?:
    | 'standard'
    | 'member'
    | 'addon'
    | 'afterhours'
    | 'member-addon'
    | 'member-afterhours'
    | 'addon-afterhours'
    | 'member-addon-afterhours'
} & ({ cdnUrl?: string; icon?: never } | { cdnUrl?: never; icon?: React.ReactNode })

export type PricebookPickerCategory<T extends PricebookPickerItem = PricebookPickerItem> = {
  id: Guid
  name: string
  items: (T | PricebookPickerCategory<T>)[]
  sectionDivider?: boolean
} & ({ cdnUrl?: string; icon?: never } | { cdnUrl?: never; icon?: React.ReactNode })

export type PricebookItems = (PricebookPickerItem | PricebookPickerCategory)[]

type PricebookProviderContextType = {
  items: PricebookItems
  rawItems: PricebookItemFragment[]
  recentItems: PricebookItems
  favoriteItems: PricebookItems
  favoriteGuids: Set<Guid>
  toggleFavorite: (guid: Guid) => void
  loaded: boolean
  refetchRecentItems: () => void
  refetchFavoriteItems: () => void
  refetch: () => void
}

const PricebookProviderContext = React.createContext<PricebookProviderContextType | undefined>(undefined)

const lowPriorityFetch: typeof fetch = (url, opts) => fetch(url, { ...opts, priority: 'low' })

const queryContext = {
  fetch: lowPriorityFetch,
}

type PricebookProviderProps = React.PropsWithChildren

export const PricebookProvider = React.memo<PricebookProviderProps>(({ children }) => {
  const principal = useExpectedPrincipal()

  const [loaded, setLoaded] = useState(false)

  const [{ data }, refetchPricebookData] = useQuery({
    query: GET_PRICEBOOK_DATA,
    pause: !principal.company || !principal.userGuid,
    context: queryContext,
  })

  const [{ data: recentItemsData }, refetchRecentItems] = useQuery({
    query: GET_RECENT_PRICEBOOK_ITEMS,
    variables: {
      userGuid: principal.userGuid,
      limit: 25,
    },
    context: queryContext,
  })

  const companyGuid = principal.company?.companyGuid

  const [{ data: favoriteItemsData }, refetchFavoriteItems] = useQuery({
    query: GET_FAVORITE_PRICEBOOK_ITEMS,
    variables: {
      userGuid: principal.userGuid,
      limit: 50,
    },
    pause: !companyGuid,
    context: queryContext,
  })

  const serverFavoriteGuids = useMemo(
    () => new Set(favoriteItemsData?.favoritePricebookItems.map(item => item.pricebookItemGuid) ?? []),
    [favoriteItemsData?.favoritePricebookItems],
  )

  const { favoriteGuids, toggleFavorite } = useToggleFavoritePricebookItem({
    serverFavoriteGuids,
    refetchFavorites: refetchFavoriteItems,
    userGuid: principal.userGuid,
    companyGuid,
  })

  useEffect(() => {
    if (data) {
      setLoaded(true)
    }
  }, [data])

  const rawItems = useMemo(() => {
    const rawItems = [...(data?.uncategorizedItems ?? [])]

    for (const category of data?.pricebookCategories ?? []) {
      rawItems.push(...category.pricebookItems)
    }

    return rawItems
  }, [data?.pricebookCategories, data?.uncategorizedItems])

  const rawItemIndex = useMemo(() => {
    const index = new Map<Guid, PricebookItemFragment>()
    for (const item of rawItems) {
      index.set(item.pricebookItemGuid, item)
    }
    return index
  }, [rawItems])

  const favoriteItems = useMemo(() => {
    // Build the list by walking the server-ordered GUIDs (createdAt DESC) first,
    // then appending any optimistically-added items not yet in server results.
    // Only include items that are in favoriteGuids (the merged server + optimistic set)
    // so that optimistic removals are respected immediately.
    const orderedGuids = favoriteItemsData?.favoritePricebookItems.map(f => f.pricebookItemGuid) ?? []

    const hydrate = (guid: Guid): PricebookPickerItem | undefined => {
      const item = rawItemIndex.get(guid)
      if (!item) return undefined
      return {
        id: item.pricebookItemGuid,
        name: item.name,
        value: item.priceUsd,
        cdnUrl: item.photo?.cdnUrl,
        ignoreSearch: true,
        subText: item.pricebookItemCode,
      }
    }

    const favItems: PricebookPickerItem[] = []
    const seen = new Set<Guid>()

    // Server-ordered items first (preserves createdAt DESC)
    for (const guid of orderedGuids) {
      if (favoriteGuids.has(guid) && !seen.has(guid)) {
        const item = hydrate(guid)
        if (item) {
          favItems.push(item)
          seen.add(guid)
        }
      }
    }

    // Optimistically-added items not yet in server results
    for (const guid of favoriteGuids) {
      if (!seen.has(guid)) {
        const item = hydrate(guid)
        if (item) {
          favItems.push(item)
          seen.add(guid)
        }
      }
    }

    const favoriteCategory: PricebookPickerCategory = {
      id: 'favorites',
      name: 'Favorite Line Items',
      items: favItems,
      icon: (
        <div className="relative mr-3 flex h-8 w-8 items-center justify-center overflow-hidden rounded border border-solid border-bz-yellow-600 bg-bz-yellow-100 p-2">
          <FontAwesomeIcon className="text-bz-yellow-600" icon={faStar} />
        </div>
      ),
    }
    return [favoriteCategory]
  }, [favoriteItemsData?.favoritePricebookItems, favoriteGuids, rawItemIndex])

  const recentItems = useMemo(() => {
    const recentItemGuids = new Set(recentItemsData?.recentPricebookItems.map(item => item.pricebookItemGuid) ?? [])

    const recentItems: PricebookPickerItem[] = rawItems
      .filter(item => recentItemGuids.has(item.pricebookItemGuid))
      .map(item => ({
        id: item.pricebookItemGuid,
        name: item.name,
        value: item.priceUsd,
        cdnUrl: item.photo?.cdnUrl,
        ignoreSearch: true,
        subText: item.pricebookItemCode,
      }))

    const recentCategory: PricebookPickerCategory = {
      id: 'recent',
      name: 'Recent Line Items',
      items: recentItems,
      sectionDivider: true,
      icon: (
        <div className="relative mr-3 flex h-8 w-8 items-center justify-center overflow-hidden rounded border border-solid border-bz-purple-600 bg-purple-100 p-2">
          <FontAwesomeIcon className="text-purple-600" icon={faClockRotateLeft} />
        </div>
      ),
    }
    return [recentCategory]
  }, [recentItemsData?.recentPricebookItems, rawItems])

  const items = useMemo<PricebookItems>(() => {
    type Datum = NotNullish<typeof data>['pricebookCategories'][number]
    const parentGuidToChildMap: Record<string, Datum[]> = {}

    const rootItems: Datum[] = []

    for (const datum of data?.pricebookCategories ?? []) {
      if (datum.parentCategoryGuid) {
        const list = parentGuidToChildMap[datum.parentCategoryGuid] || []
        list.push(datum)
        parentGuidToChildMap[datum.parentCategoryGuid] = list
      } else {
        rootItems.push(datum)
      }
    }

    const populateChildren = (categories: Datum[]): PricebookPickerCategory[] =>
      categories.map(category => {
        const ourItems: PricebookPickerItem[] = category.pricebookItems.map(item => ({
          id: item.pricebookItemGuid,
          name: item.name,
          value: item.priceUsd,
          cdnUrl: item.photo?.cdnUrl,
          subText: item.pricebookItemCode,
        }))
        const childCategoryItems: PricebookPickerCategory[] = populateChildren(
          parentGuidToChildMap[category.pricebookCategoryGuid] ?? [],
        )

        return {
          id: category.pricebookCategoryGuid,
          name: category.name,
          cdnUrl: category.photo?.cdnUrl,
          items: [...ourItems, ...childCategoryItems],
        }
      })

    const categories = populateChildren(rootItems)

    const uncategorizedItems =
      data?.uncategorizedItems.map(item => ({
        id: item.pricebookItemGuid,
        name: item.name,
        value: item.priceUsd,
        cdnUrl: item.photo?.cdnUrl,
        subText: item.pricebookItemCode,
      })) ?? []

    return [...favoriteItems, ...recentItems, ...categories, ...uncategorizedItems]
  }, [data?.pricebookCategories, data?.uncategorizedItems, favoriteItems, recentItems])

  const refetch = useCallback(() => {
    refetchPricebookData({ requestPolicy: 'network-only' })
  }, [refetchPricebookData])

  return (
    <PricebookProviderContext.Provider
      value={{
        items,
        rawItems,
        recentItems,
        favoriteItems,
        favoriteGuids,
        toggleFavorite,
        loaded,
        refetchRecentItems,
        refetchFavoriteItems,
        refetch,
      }}
    >
      {children}
    </PricebookProviderContext.Provider>
  )
})

PricebookProvider.displayName = 'PricebookProvider'

export const usePricebook = () => useStrictContext(PricebookProviderContext)
